import { VStack, Card } from '@chakra-ui/react';
import {
  CustomerPortalBillGroup,
  CustomerPortalData,
  CustomerPortalPaymentMethod,
  CustomerPortalSessionData,
  PaymentMethodStatusEnum,
  PaymentMethodTypeEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { endOfMonth } from 'date-fns/endOfMonth';
import { useState } from 'react';
import { useUpdateCustomerPortalBillGroup } from '../../../../../api/customerPortalService';
import {
  MButton,
  MCard,
  MFlex,
  MText,
} from '../../../../../components/Monetize';
import { NewPaymentMethodModal } from '../NewPaymentMethodModal';
import { PaymentMethodBankTransfer } from './PaymentMethodBankTransfer';
import { PaymentMethodRow } from './PaymentMethodRow';

const isExpiredCard = (pm: CustomerPortalPaymentMethod) => {
  if (pm.paymentMethodType !== PaymentMethodTypeEnum.CREDIT_CARD) {
    return false;
  }
  if (!pm.expirationYear || !pm.expirationMonth) {
    return false;
  }
  // A card with expiration MM/YYYY is valid through the last moment of that month
  // in the user's local timezone.
  const expirationDate = endOfMonth(
    new Date(pm.expirationYear, pm.expirationMonth - 1, 1),
  );
  return new Date() > expirationDate;
};

interface PaymentMethodsCardProps {
  sessionData: CustomerPortalSessionData;
  billGroup: CustomerPortalBillGroup;
  paymentMethods: CustomerPortalPaymentMethod[];
  paymentGatewayId?: string;
  defaultPaymentMethodId: Maybe<string>;
  hasLoadError: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  permissions: CustomerPortalData['permissions']['paymentMethods'];
}

export const PaymentMethodsCard = ({
  sessionData,
  billGroup,
  defaultPaymentMethodId,
  paymentMethods,
  paymentGatewayId,
  hasLoadError,
  hasMore,
  isLoadingMore,
  onLoadMore,
  permissions,
}: PaymentMethodsCardProps) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { mutateAsync: updateBillGroup, isPending: isSettingDefault } =
    useUpdateCustomerPortalBillGroup({
      meta: { showErrorToast: true },
    });

  const handleAddClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleSetAsDefault = async (paymentMethodId: string) => {
    await updateBillGroup({
      sessionData,
      payload: { id: billGroup.id, paymentMethodId },
    });
  };

  const activePaymentMethods = paymentMethods.filter(
    ({ paymentMethodType, status }) =>
      paymentMethodType !== PaymentMethodTypeEnum.US_BANK_TRANSFER &&
      status === PaymentMethodStatusEnum.ACTIVE,
  );

  const achWireTransferPaymentMethod = paymentMethods.find(
    ({ paymentMethodType, status }) =>
      paymentMethodType === PaymentMethodTypeEnum.US_BANK_TRANSFER &&
      status === PaymentMethodStatusEnum.ACTIVE,
  );

  const showEmptyState =
    activePaymentMethods.length === 0 && !achWireTransferPaymentMethod;
  const showErrorState = hasLoadError;
  const showContent = !showEmptyState && !showErrorState;

  return (
    <MCard variant="border" borderRadius="md">
      <Card.Header pt={4} pb={2} px={4}>
        <MFlex justifyContent="space-between" alignItems="center">
          <MText fontWeight="bold" fontSize="lg" color="tPurple.dark">
            Payment Methods
          </MText>
          {permissions.create && (
            <MButton
              variant="tertiary"
              size="sm"
              onClick={handleAddClick}
              isDisabled={!paymentGatewayId}
            >
              New
            </MButton>
          )}
        </MFlex>
      </Card.Header>
      <Card.Body pt={0} px={4} pb={4}>
        <VStack gap={2} align="stretch">
          {showEmptyState && (
            <MFlex align="center" justify="center" py={8}>
              <MText color="tGray.darkPurple">No payment methods found</MText>
            </MFlex>
          )}
          {showErrorState && (
            <MFlex align="center" justify="center" py={8}>
              <MText color="tGray.darkPurple">
                Unable to load payment methods
              </MText>
            </MFlex>
          )}
          {showContent && (
            <>
              {achWireTransferPaymentMethod && (
                <PaymentMethodBankTransfer
                  paymentMethod={achWireTransferPaymentMethod}
                />
              )}
              {activePaymentMethods.map((paymentMethod) => {
                const isDefault = paymentMethod.id === defaultPaymentMethodId;
                const canSetAsDefault =
                  permissions.changeDefault.edit &&
                  !isDefault &&
                  !isExpiredCard(paymentMethod);
                return (
                  <PaymentMethodRow
                    key={paymentMethod.id}
                    paymentMethod={paymentMethod}
                    isDefault={isDefault && permissions.changeDefault.view}
                    canSetAsDefault={canSetAsDefault}
                    isSettingDefault={isSettingDefault}
                    onSetAsDefault={handleSetAsDefault}
                  />
                );
              })}
            </>
          )}
        </VStack>
        {hasMore && (
          <MFlex justifyContent="center" pt={2}>
            <MButton
              type="button"
              variant="tertiary"
              onClick={onLoadMore}
              isLoading={isLoadingMore}
              isDisabled={isLoadingMore}
            >
              Load More
            </MButton>
          </MFlex>
        )}
      </Card.Body>
      <NewPaymentMethodModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseAddModal}
        sessionData={sessionData}
        paymentGatewayId={paymentGatewayId}
        defaultBillingDetails={billGroup.billingContact}
        canChangeDefault={permissions.changeDefault.edit}
      />
    </MCard>
  );
};
